import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { modulesApi, type ModuleInfo } from '@/api/modules';
import { createMutationErrorHandler } from '@/lib/mutationErrorHandler';
import { useToast } from './useToast';

export const moduleKeys = {
  all: ['modules'] as const,
  list: () => [...moduleKeys.all, 'list'] as const,
};

/**
 * Fetch installed modules + their runtime enabled/errored state.
 */
export function useModules(): UseQueryResult<ModuleInfo[], Error> {
  return useQuery({
    queryKey: moduleKeys.list(),
    queryFn: () => modulesApi.list(),
    staleTime: 60_000,
  });
}

/**
 * Convenience: is a given module currently enabled? Returns false while loading
 * or on error (fail-closed).
 */
export function useModuleEnabled(id: string): boolean {
  const { data } = useModules();
  return data?.some((m) => m.id === id && m.enabled) ?? false;
}

/**
 * Toggle a module's enabled flag (Settings → Modules).
 */
export function useSetModuleEnabled(): UseMutationResult<
  { id: string; enabled: boolean },
  Error,
  { id: string; enabled: boolean }
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, enabled }) => modulesApi.setEnabled(id, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: moduleKeys.all });
    },
    onError: createMutationErrorHandler(toast),
  });
}
